import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUser, FiMail, FiSettings, FiSave, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getUserDetails, getMentorSettings, updateMentorSettings } from '../../services/adminService';

const MentorManagement = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [settings, setSettings] = useState({
    mentee_status: '',
    gift_credits: 0
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMentorData = async () => {
      try {
        setLoading(true);
        
        // Fetch mentor basic info
        const mentorResponse = await getUserDetails(mentorId);
        setMentor(mentorResponse.data);
        
        // Fetch mentor settings
        const settingsResponse = await getMentorSettings(mentorId);
        setSettings(settingsResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching mentor data:', err);
        setError('Failed to load mentor data. Please try again later.');
        setLoading(false);
      }
    };

    fetchMentorData();
  }, [mentorId]);

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateSettings = async () => {
    try {
      setUpdating(true);
      await updateMentorSettings(mentorId, settings);
      toast.success('Mentor settings updated successfully');
      setUpdating(false);
    } catch (err) {
      console.error('Error updating mentor settings:', err);
      toast.error('Failed to update mentor settings. Please try again.');
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Mentor Management</h1>
        <button
          type="button"
          onClick={() => navigate('/admin/users')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <FiArrowLeft className="mr-2" />
          Back to Users
        </button>
      </div>

      {/* Mentor Profile */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Mentor Profile</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Basic mentor information.</p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiUser className="mr-2" /> Full Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{mentor?.full_name}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiMail className="mr-2" /> Email
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{mentor?.email}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Mentor Settings */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
            <FiSettings className="mr-2" />
            Mentor Settings
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Configure mentor's default settings for mentees.</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="space-y-6">
            {/* Mentee Status */}
            <div className="sm:grid sm:grid-cols-3 sm:gap-4">
              <div className="text-sm font-medium text-gray-500">Default Mentee Status</div>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <select
                  value={settings.mentee_status}
                  onChange={(e) => handleSettingsChange('mentee_status', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                >
                  <option value="">Select status</option>
                  <option value="community">Community</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  Default membership status for new mentees assigned to this mentor.
                </p>
              </div>
            </div>

            {/* Gift Credits */}
            <div className="sm:grid sm:grid-cols-3 sm:gap-4">
              <div className="text-sm font-medium text-gray-500">Default Gift Credits</div>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="number"
                  min="0"
                  value={settings.gift_credits}
                  onChange={(e) => handleSettingsChange('gift_credits', parseInt(e.target.value) || 0)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter credits amount"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Default number of credits to gift to new mentees assigned to this mentor.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUpdateSettings}
                disabled={updating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <FiSave className="mr-2" />
                {updating ? 'Updating...' : 'Update Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorManagement;